import useDeleteWatch from '@/api/core/watches/useDeleteWatch';
import { WatcherDTO } from 'src/types/application/WatcherDTO';
import { AvatarWithFallback } from '@/components/users/AvatarWithFallback';
import { cn } from '@/lib/utils';
import { UserCheckIcon, TrashIcon, StarIcon } from '@phosphor-icons/react';
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { T, Var, useGT } from 'gt-react';

interface WatcherListItemProps {
  watcher: WatcherDTO;
  watchableEntityType: string;
  watchableId: string;
  handleToggleWatchKind: (watcher: WatcherDTO) => void;
  isAssignedSection: boolean;
  /** Fired after a successful remove, so owners can refresh derived state. */
  onRemoved?: (watcher: WatcherDTO) => void;
}

export const WatcherListItem = ({
  watcher,
  watchableEntityType,
  watchableId,
  handleToggleWatchKind,
  isAssignedSection,
  onRemoved,
}: WatcherListItemProps) => {
  const gt = useGT();
  const deleteWatch = useDeleteWatch({
    watchableEntityType: watchableEntityType,
    watchableId: watchableId,
    watchId: watcher.watchId,
  });

  const handleAssignClick = () => {
    if (watcher.watchKind !== 'assigned') {
      handleToggleWatchKind(watcher);
    }
  };

  const handleRemoveClick = async () => {
    try {
      await deleteWatch.mutateAsync({
        watchableEntityType,
        watchableId,
        watchId: watcher.watchId,
      });
      onRemoved?.(watcher);
    } catch (error) {
      console.error('Failed to remove watcher:', error);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-2 rounded-md gap-3',
        isAssignedSection ? 'hover:bg-blue-50' : 'hover:bg-gray-50',
      )}
    >
      <div className="flex items-center gap-3 flex-shrink min-w-0">
        <AvatarWithFallback user={watcher} size="sm" />
        <div className="min-w-0">
          <div className="font-medium text-sm truncate" title={watcher.name}>
            {watcher.name}
          </div>
          <div
            className="text-xs text-gray-500 truncate"
            title={watcher.emailOverride ?? watcher.email}
          >
            {watcher.emailOverride ?? watcher.email}
          </div>
          {watcher.emailOverride && (
            <div
              className="text-[10px] text-amber-600 mt-0.5"
              title={gt(
                'Notifications are routed to this address instead of {email}',
                {
                  email: watcher.email,
                },
              )}
            >
              <T>Notifications routed to override</T>
            </div>
          )}
          {watcher.watchRole && (
            <div className="text-[10px] text-purple-600 mt-0.5">
              {watcher.watchRole}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {isAssignedSection ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <UserCheckIcon className="h-3 w-3" />
            <span>
              <T>Assigned</T>
            </span>
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAssignClick}
                className="h-7 px-2 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50"
              >
                <StarIcon className="h-3 w-3 mr-1" />
                <T>Assign</T>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <T>
                  Assign to <Var>{watcher.name}</Var>
                </T>
              </p>
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveClick}
              className="h-7 w-7 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
              disabled={deleteWatch.isLoading}
            >
              <TrashIcon className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{gt('Remove')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
